import { Button } from '@fountain/fountain-ui-components';
import { CircularProgress, Typography } from '@material-ui/core';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import messages from './messages';
import { useStyles } from './styles';

interface BulkDecisionBarProps {
  count: number;
  running: boolean;
  onApprove: () => void;
  onReject: () => void;
  onClear: () => void;
}

/**
 * Approve or reject every selected opening at once (Design Requirements §5).
 *
 * ## In the flow, not floating over it
 *
 * Between the toolbar and the table rather than pinned to the viewport. A floating
 * bar covers the last row of a 25-row page, which is a row the reader may be about
 * to tick — and `layout-composition` keeps a contextual action in the region it
 * acts on. The bar appears only while something is selected, so it costs nothing
 * when it is not in use.
 *
 * ## It says the count, and the count is the whole point
 *
 * The reader is about to decide several things at once, so the number is the
 * heading rather than a detail beside the buttons. Announced with `role="status"`:
 * ticking a checkbox produces no other feedback that the count moved.
 *
 * Reject keeps the negative secondary weight it has on a single row —
 * `constraint-anti-pattern-guard` prohibits a destructive action in the primary
 * position, and this one is a rejection multiplied.
 *
 * ## Clear, Reject, Approve — the row's order, not the prototype's
 *
 * A deliberate divergence, decided rather than overlooked. The prototype leads
 * with `Approve all` and ends with `Clear`, and AXHE-5031 lists button order as
 * in scope. Its reason for the prototype's order was that the order matched the
 * row actions' Approve-then-Reject — which was true when it was filed and is not
 * now: `OpeningActionsCell` shipped Reject-then-Approve under AXHE-5025. So
 * following the prototype today would produce the split the ticket's own
 * argument was against, on one screen, between a row and the bar above it.
 *
 * The guard does not settle it either way: it prohibits a destructive action in
 * the *primary* position, and neither order does that.
 *
 * The labels do come from the prototype: "all" is what tells the reader this
 * button is not the one on the row beside it.
 */
export const BulkDecisionBar: React.FC<BulkDecisionBarProps> = ({
  count,
  running,
  onApprove,
  onReject,
  onClear,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  return (
    <div className={styles.bulkBar}>
      <Typography className={styles.bulkCount} role="status" variant="body2">
        <FormattedMessage {...messages.bulkSelected} values={{ count }} />
      </Typography>

      <div className={styles.bulkActions}>
        {/* Leaves the rows alone, so it is neither of the two decisions and does
            not sit with them. */}
        <Button
          autoWidth
          size="small"
          type="text"
          disabled={running}
          onClick={onClear}
        >
          <FormattedMessage {...globalMessages.clear} />
        </Button>
        <Button
          autoWidth
          negative
          className={styles.destructiveButton}
          size="small"
          type="secondary"
          disabled={running}
          onClick={onReject}
        >
          <FormattedMessage {...messages.bulkRejectAll} />
        </Button>
        <Button
          autoWidth
          size="small"
          type="primary"
          // Named explicitly because the label is swapped for a spinner while the
          // run is in flight, which would otherwise leave the control with no
          // accessible name at the moment somebody is waiting to hear what
          // happened. Same reason the row's own Approve carries one.
          aria-label={intl.formatMessage(messages.bulkApproveAll)}
          disabled={running}
          onClick={onApprove}
        >
          {running ? (
            <CircularProgress size={16} />
          ) : (
            <FormattedMessage {...messages.bulkApproveAll} />
          )}
        </Button>
      </div>
    </div>
  );
};
